/* Erzeugt aus packages/ui/src/tokens.ts — nicht von Hand ändern.
   Neu erzeugen: pnpm --filter @hausmeter/ui design:emit
   Die Quelle ist TypeScript, weil Web und Mobile dieselben Zahlen brauchen. */

/* Wird von apps/web über `import "@hausmeter/ui/theme.css"` geladen.

   Die Schriften kommen aus @fontsource — self-hosted und vom Bundler
   ausgeliefert, nicht von einem fremden Server. Ein Abruf bei Google beim
   Seitenaufruf passt nicht zu einem Produkt, das Local-First verspricht, und
   ist in der EU datenschutzrechtlich heikel. Lizenzen (SIL OFL 1.1) unter
   docs/licenses/fonts/. */

@import "@fontsource-variable/sora";
@import "@fontsource-variable/manrope";
@import "@fontsource-variable/jetbrains-mono";

:root {
  color-scheme: light;

  /* ---- Marke ---- */
  --hm-green-50: #ecfaf0;
  --hm-green-100: #d1f3dc;
  --hm-green-200: #a6e6bd;
  --hm-green-300: #6fd394;
  --hm-green-400: #38bd6c;
  --hm-green-500: #1aa84f;
  --hm-green-600: #128a41;
  --hm-green-700: #0f6f36;
  --hm-green-800: #0f572d;
  --hm-green-900: #0c3f22;
  --hm-yellow-50: #fef9e6;
  --hm-yellow-100: #fdf0bf;
  --hm-yellow-200: #fbe285;
  --hm-yellow-300: #fad251;
  --hm-yellow-400: #fbcb1e;
  --hm-yellow-500: #e6b40a;
  --hm-yellow-600: #b88a06;

  /* ---- Neutrale ---- */
  --hm-slate-0: #ffffff;
  --hm-slate-50: #f8fafc;
  --hm-slate-100: #f1f5f9;
  --hm-slate-200: #e2e8f0;
  --hm-slate-300: #cbd5e1;
  --hm-slate-400: #94a3b8;
  --hm-slate-500: #64748b;
  --hm-slate-600: #475569;
  --hm-slate-700: #334155;
  --hm-slate-800: #1e293b;
  --hm-slate-900: #0f172a;

  /* ---- Achse 2: Größen. Die einzigen Serienfarben. ---- */
  --hm-quantity-consumption: #334155;
  --hm-quantity-grid-import: #c2410c;
  --hm-quantity-grid-export: #0369a1;
  --hm-quantity-generation: #ca8a04;
  --hm-quantity-self-consumption: #15803d;
  --hm-quantity-common-area: #64748b;
  --hm-quantity-unit-consumption: #334155;
  --hm-quantity-unit-pv-share: #15803d;
  --hm-quantity-unit-grid-share: #c2410c;
  --hm-quantity-load-consumption: #334155;
  --hm-quantity-load-pv-share: #15803d;
  --hm-quantity-residual-consumption: #334155;
  --hm-quantity-storage-charge: #5b21b6;
  --hm-quantity-storage-discharge: #8b5cf6;
  --hm-quantity-consumption-2: #64748b;
  --hm-quantity-consumption-3: #94a3b8;
  --hm-quantity-consumption-rest: #cbd5e1;

  /* Reihe ohne gedeutete Größe — der Rohverlauf eines Datenpunkts. */
  --hm-series-datapoint: #7e22ce;

  /* ---- Achse 3: Sparten. Nur Wegweisung. ---- */
  --hm-commodity-electricity: #fbcb1e;
  --hm-commodity-water: #2e90d9;
  --hm-commodity-heat: #e8683a;
  --hm-commodity-gas: #7c3aed;

  /* ---- Zustand einer Reihe ---- */
  /* Eine Lücke wird geführt, nicht gefüllt: sichtbar, aber nicht alarmierend. */
  --hm-gap: #cbd5e1;
  /* Nicht gemessen, sondern gerechnet — immer gekennzeichnet. */
  --hm-quality-interpolated: #94a3b8;
  --hm-quality-estimated: #94a3b8;
  --hm-quality-suspect: #b45309;

  /* ---- Status ---- */
  --hm-success: #15803d;
  --hm-warning: #b45309;
  --hm-danger: #b91c1c;
  --hm-info: #0369a1;

  /* ---- Tonpaare für Hinweisflächen (Alert, Badge, Warnkästen) ----
     Fläche, Rand und Schrift eines Tons gehören zusammen; einzeln gemischt
     entstehen sonst in jedem Bild andere Kombinationen. */
  --tone-success-bg: #ecfaf0;
  --tone-success-border: #a6e6bd;
  --tone-success-fg: #0f6f36;
  --tone-warning-bg: #fdf3e4;
  --tone-warning-border: #f3ddb8;
  --tone-warning-fg: #b45309;
  --tone-danger-bg: #fdeceb;
  --tone-danger-border: #f4c8c5;
  --tone-danger-fg: #b12a25;
  --tone-info-bg: #e8f2fa;
  --tone-info-border: #bcd8ee;
  --tone-info-fg: #0f4d7a;

  /* ---- Semantische Aliase ---- */
  --text-strong: var(--hm-slate-900);
  --text-body: var(--hm-slate-700);
  --text-muted: var(--hm-slate-500);
  --text-faint: var(--hm-slate-400);
  --text-invert: var(--hm-slate-0);
  --text-brand: var(--hm-green-600);

  --surface-page: var(--hm-slate-50);
  --surface-card: var(--hm-slate-0);
  --surface-sunken: var(--hm-slate-100);
  --surface-inset: var(--hm-slate-50);
  --surface-brand: var(--hm-green-500);
  /* Die tiefe Markenfläche — bleibt im dunklen Thema, was sie hell ist. */
  --surface-brand-strong: var(--hm-green-600);
  --surface-dark: var(--hm-slate-900);
  /* Kurzhinweis am Zeiger: dunkle Fläche im hellen Bild, helle im dunklen. */
  --surface-tooltip: #0f172a;
  --text-on-tooltip: #ffffff;
  /* Der Knopf eines Schalters — nicht „weiß", sondern „das Helle auf dem Zug". */
  --surface-knob: #ffffff;

  --border-subtle: var(--hm-slate-200);
  --border-default: var(--hm-slate-300);
  --border-strong: var(--hm-slate-400);
  --border-brand: var(--hm-green-500);

  --focus-ring: color-mix(in srgb, var(--hm-green-500) 45%, transparent);

  /* Der Pfeil des Select — je Thema eingebrannt, siehe emit.ts. */
  --select-chevron: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='14' height='14' viewBox='0 0 14 14' fill='none'%3E%3Cpath d='M3.5 5.5L7 9L10.5 5.5' stroke='%2364748b' stroke-width='1.6' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");

  --font-display: 'Sora Variable', Sora, 'Segoe UI', system-ui, sans-serif;
  --font-body: 'Manrope Variable', Manrope, 'Segoe UI', system-ui, sans-serif;
  --font-mono: 'JetBrains Mono Variable', 'JetBrains Mono', ui-monospace, 'SF Mono', monospace;

  /* Größenleiter (px) */
  --fs-display: 40px;
  --fs-h1: 32px;
  --fs-h2: 24px;
  --fs-h3: 20px;
  --fs-lg: 20px;
  --fs-base: 16px;
  --fs-sm: 14px;
  --fs-xs: 12px;

  /* Zahlen (Monoschrift) */
  --fs-metric-xl: 40px;
  --fs-metric-lg: 32px;
  --fs-metric-md: 24px;

  --lh-tight: 1.12;
  --lh-snug: 1.3;
  --lh-normal: 1.5;
  --lh-relaxed: 1.65;

  --fw-regular: 400;
  --fw-medium: 500;
  --fw-semibold: 600;
  --fw-bold: 700;
  --fw-extrabold: 800;

  --tracking-tight: -0.02em;
  --tracking-snug: -0.01em;
  --tracking-normal: 0em;
  --tracking-wide: 0.04em;
  --tracking-caps: 0.08em;

  /* Abstände */
  --sp-1: 4px;
  --sp-2: 8px;
  --sp-3: 12px;
  --sp-4: 16px;
  --sp-5: 20px;
  --sp-6: 24px;
  --sp-7: 32px;
  --sp-8: 40px;
  --sp-9: 48px;
  --sp-10: 64px;
  --sp-12: 80px;

  /* Radien — weich, dem Logo-Quadrat nachempfunden */
  --radius-xs: 4px;
  --radius-sm: 8px;
  --radius-md: 12px;
  --radius-lg: 16px;
  --radius-xl: 24px;
  --radius-pill: 999px;

  /* Schatten — weich, kühl getönt, wenig Streuung */
  --shadow-xs: 0 1px 2px rgba(15, 23, 42, 0.06);
  --shadow-sm: 0 1px 3px rgba(15, 23, 42, 0.08), 0 1px 2px rgba(15, 23, 42, 0.04);
  --shadow-md: 0 4px 12px rgba(15, 23, 42, 0.08), 0 2px 4px rgba(15, 23, 42, 0.04);
  --shadow-lg: 0 12px 28px rgba(15, 23, 42, 0.12), 0 4px 8px rgba(15, 23, 42, 0.05);
  --shadow-brand: 0 6px 18px rgba(26, 168, 79, 0.28);

  /* Fokus */
  --ring-width: 3px;

  /* Der Grund hinter Dialog und Schublade */
  --overlay-scrim: rgb(15 23 42 / 45%);

  /* Gerüst (nur Web) */
  --container-max: 1200px;
  --sidebar-w: 248px;
  --header-h: 64px;

  /* Gerüst der App — Telefon und Tablet (iOS, Android; ADR 0052) */
  --app-regular: 600px;
  --app-rail-w: 80px;
  --app-gutter: 24px;
  --app-measure-form: 480px;
  --app-measure-reading: 720px;
  --app-measure-wide: 1040px;
  --app-column-min: 320px;
  --app-sheet-max: 640px;

  /* ---- Übergang: die Namen aus der ersten Fassung ----
     Sie stammen aus einem Entwurf, der ohne Codebasis entstand, und stehen
     hier nur, damit die vorhandenen Komponenten weiterlaufen.

     --hm-solar war der schwerste Fehler: Erzeugung ist keine Sparte, sondern
     eine Größe, und sie trug die Markenfarbe. Der Alias zeigt jetzt auf die
     Größenfarbe — Diagramme ändern dadurch ihr Aussehen. Das ist die Korrektur,
     nicht der Schaden.

     Entfällt mit HM-010. */
  --hm-strom: var(--hm-commodity-electricity);
  --hm-wasser: var(--hm-commodity-water);
  --hm-heizung: var(--hm-commodity-heat); /* Sparte heißt Wärme, nicht Heizung */
  --hm-solar: var(--hm-quantity-generation);
  --hm-strom-ink: #7a5c00;
  --hm-wasser-ink: #0f4d7a;
  --hm-heizung-ink: #8a3212;
  --hm-solar-ink: var(--hm-quantity-self-consumption);
}

/* HausMeter — dunkles Erscheinungsbild (HM-107, ADR 0030).

   Ein Umschalter, keine zweite Farbwelt: Gesetzt wird `data-theme="dark"` auf
   <html>. Überschrieben werden ausschließlich
     — die neutralen Stufen (--hm-slate-*),
     — die Tint- und Tiefstufen der Marke (green-50/100/…, yellow-50/600),
     — die semantischen Aliase (Flächen, Text, Ränder, Schatten),
     — die Serien- und Spartenfarben in einer helleren Fassung.

   Warum auch die Marken-Tints: Zustände wie „aktiv" liegen als --hm-green-50
   hinter dem Element und als --hm-green-700 darauf. Auf dunklem Grund muss das
   Paar die Rollen tauschen, sonst leuchtet eine weiße Fläche in der Navigation.
   Die Vollstufe --hm-green-500 bleibt unverändert: Ein Primärknopf ist in
   beiden Fassungen dasselbe Grün.

   Die Achsentrennung gilt weiter — Größen und Sparten bleiben farblich
   disjunkt, nur heller.

   Der Selektor greift auf jedem Element, nicht nur auf <html>: Damit lässt sich
   ein einzelner Bereich dunkel stellen (Vergleichskarten, eingebettete
   Vorschau). Im Produkt steht das Attribut auf <html>. */

[data-theme="dark"] {
  color-scheme: dark;

  /* ---- Neutrale: Leiter gespiegelt ---- */
  --hm-slate-0: #0b1220;
  --hm-slate-50: #0f1725;
  --hm-slate-100: #17202f;
  --hm-slate-200: #222c3e;
  --hm-slate-300: #313d53;
  --hm-slate-400: #6b7b93;
  --hm-slate-500: #8fa0b6;
  --hm-slate-600: #adbbcd;
  --hm-slate-700: #c9d4e2;
  --hm-slate-800: #e1e8f1;
  --hm-slate-900: #f2f6fb;

  /* ---- Marke: Tints werden Tiefen, Tiefen werden Tints. 500 bleibt. ---- */
  --hm-green-50: #10281c;
  --hm-green-100: #16382a;
  --hm-green-200: #1d5138;
  --hm-green-300: #2a7a4f;
  --hm-green-400: #38bd6c;
  --hm-green-600: #5ecb88;
  --hm-green-700: #8fdda9;
  --hm-green-800: #b6e9c7;
  --hm-green-900: #d9f5e2;
  --hm-yellow-50: #2a2208;
  --hm-yellow-100: #3b300b;
  --hm-yellow-600: #f6d264;

  /* ---- Text ---- */
  --text-strong: #f2f6fb;
  --text-body: #c9d4e2;
  --text-muted: #8fa0b6;
  --text-faint: #7c8ba3;
  /* Unverändert: Die Fläche darunter ist das Markengrün, nicht das Thema. */
  --text-invert: #ffffff;
  --text-brand: #5ecb88;

  /* ---- Flächen: die Karte liegt über der Seite, also heller ---- */
  --surface-page: #0a101c;
  --surface-card: #141d2c;
  --surface-sunken: #1d2738;
  --surface-inset: #111a28;
  --surface-brand: #1aa84f;
  --surface-brand-strong: #128a41;
  --surface-dark: #050a13;
  --surface-tooltip: #f2f6fb;
  --text-on-tooltip: #0a101c;
  --surface-knob: #ffffff;

  /* ---- Ränder ---- */
  --border-subtle: #222c3e;
  --border-default: #2d3950;
  --border-strong: #44536d;
  --border-brand: #1aa84f;

  --focus-ring: color-mix(in srgb, #38bd6c 55%, transparent);

  /* ---- Achse 2: Größen, heller gefasst ---- */
  --hm-quantity-consumption: #c9d4e2;
  --hm-quantity-grid-import: #fb9a4b;
  --hm-quantity-grid-export: #4cb8ea;
  --hm-quantity-generation: #f2c33c;
  --hm-quantity-self-consumption: #4ec97d;
  --hm-quantity-common-area: #94a3b8;
  --hm-quantity-unit-consumption: #c9d4e2;
  --hm-quantity-unit-pv-share: #4ec97d;
  --hm-quantity-unit-grid-share: #fb9a4b;
  --hm-quantity-load-consumption: #c9d4e2;
  --hm-quantity-load-pv-share: #4ec97d;
  --hm-quantity-residual-consumption: #c9d4e2;
  --hm-quantity-storage-charge: #b08cf9;
  --hm-quantity-storage-discharge: #d0bcff;
  --hm-quantity-consumption-2: #8ba0b8;
  --hm-quantity-consumption-3: #73889f;
  --hm-quantity-consumption-rest: #5c7089;
  --hm-series-datapoint: #c084fc;

  /* ---- Achse 3: Sparten ---- */
  --hm-commodity-electricity: #fbcb1e;
  --hm-commodity-water: #4fb0ef;
  --hm-commodity-heat: #f58a5e;
  --hm-commodity-gas: #a78bfa;

  /* ---- Zustand einer Reihe ---- */
  --hm-gap: #3a4760;
  --hm-quality-interpolated: #7c8ba3;
  --hm-quality-estimated: #7c8ba3;
  --hm-quality-suspect: #e0a94a;

  /* ---- Status ---- */
  --hm-success: #4ec97d;
  --hm-warning: #e9a83c;
  --hm-danger: #f0706b;
  --hm-info: #4cb8ea;

  /* ---- Tonpaare für Hinweisflächen ---- */
  --tone-success-bg: #10281c;
  --tone-success-border: #1d5138;
  --tone-success-fg: #8fdda9;
  --tone-warning-bg: #2d2209;
  --tone-warning-border: #4a3a13;
  --tone-warning-fg: #f6cf7a;
  --tone-danger-bg: #2e1416;
  --tone-danger-border: #4d2024;
  --tone-danger-fg: #f7a8a4;
  --tone-info-bg: #0e2433;
  --tone-info-border: #1b3d54;
  --tone-info-fg: #8ed0f2;

  /* ---- Schatten: auf dunklem Grund trägt Tiefe, nicht Streuung ---- */
  --shadow-xs: 0 1px 2px rgba(0, 0, 0, 0.4);
  --shadow-sm: 0 1px 3px rgba(0, 0, 0, 0.45), 0 1px 2px rgba(0, 0, 0, 0.3);
  --shadow-md: 0 4px 12px rgba(0, 0, 0, 0.5), 0 2px 4px rgba(0, 0, 0, 0.3);
  --shadow-lg: 0 12px 28px rgba(0, 0, 0, 0.55), 0 4px 8px rgba(0, 0, 0, 0.35);
  --shadow-brand: 0 6px 18px rgba(26, 168, 79, 0.35);

  --select-chevron: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='14' height='14' viewBox='0 0 14 14' fill='none'%3E%3Cpath d='M3.5 5.5L7 9L10.5 5.5' stroke='%238fa0b6' stroke-width='1.6' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");

  /* ---- Übergangsaliase (siehe oben) ---- */
  --hm-strom-ink: #f2c33c;
  --hm-wasser-ink: #8ed0f2;
  --hm-heizung-ink: #f4a184;
}

/* Bildmarke Christians Technikshop: Haare und Konturen sind schwarz. Im dunklen
   Erscheinungsbild bekommt sie eine feine helle Kontur statt eines hellen Grunds
   (Design-System, tokens/theme-dark.css). */
[data-theme="dark"] .hm-ct-mark {
  filter: drop-shadow(0 0 0.6px rgba(255, 255, 255, 0.9)) drop-shadow(0 0 0.6px rgba(255, 255, 255, 0.9));
}

*,
*::before,
*::after {
  box-sizing: border-box;
}

html,
body,
#root {
  height: 100%;
}

body {
  margin: 0;
  background: var(--surface-page);
  color: var(--text-body);
  font-family: var(--font-body);
  font-size: var(--fs-base);
  line-height: var(--lh-normal);
  -webkit-font-smoothing: antialiased;
}

h1,
h2,
h3 {
  font-family: var(--font-display);
  color: var(--text-strong);
  letter-spacing: var(--tracking-tight);
  margin: 0;
}

/* Jede Zahl steht in der Monoschrift — gleiche Ziffernbreite, damit eine
   Zahlenspalte beim Aktualisieren nicht springt. */
code,
kbd,
samp,
.hm-metric {
  font-family: var(--font-mono);
  font-variant-numeric: tabular-nums;
}

/* Der Fokusring trägt die Markenfarbe: Er ist Navigation, keine Datenaussage.
   `:focus-visible` statt `:focus`, damit ein Mausklick keinen Ring hinterlässt. */
:focus-visible {
  outline: var(--ring-width) solid var(--focus-ring);
  outline-offset: 2px;
}

/* Der Grund hinter einem modalen Dialog. Die Regel steht hier und nicht in der
   Komponente, weil `::backdrop` ein Pseudoelement ist — ein `style`-Objekt in
   React erreicht es nicht. Die Vorgabe des Browsers ist so blass, dass der
   Dialog zu schweben scheint, statt den Rest stillzulegen; genau das soll er
   aber zeigen. Die Farbe kommt aus dem Token, weil die Schublade der
   Seitenleiste denselben Grund braucht. */
dialog::backdrop {
  background: var(--overlay-scrim);
}

/* Ein Dialog liegt in der obersten Ebene und scrollt nicht mit. Ohne das
   scrollt der Inhalt dahinter weiter, während der Dialog stehen bleibt. */
body:has(dialog[open]) {
  overflow: hidden;
}

/* Schrift in Eingabefeldern. Safari auf iPhone und iPad zoomt beim Antippen in
   jedes Feld unter 16 px hinein — und zoomt danach nicht zurück: Wer sich
   angemeldet hat, sah die ganze Anwendung leicht vergrößert. Mit Finger
   bedient, stehen Felder deshalb in 16 px; mit Maus bleibt es bei den Maßen des
   Entwurfs. `maximum-scale=1` im Viewport wäre der kürzere Weg und nähme auf
   Android das Vergrößern mit zwei Fingern (WCAG 1.4.4). */
:root {
  --field-font-md: 14px;
  --field-font-sm: 13px;
}
@media (pointer: coarse) {
  :root {
    --field-font-md: 16px;
    --field-font-sm: 16px;
  }
}

/* Bewegung ist kurz und funktional. Wer sie abbestellt hat, bekommt keine. */
@media (prefers-reduced-motion: reduce) {
  *,
  *::before,
  *::after {
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.01ms !important;
    scroll-behavior: auto !important;
  }
}
